import {
  ChangeDetectorRef,
  Component,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DocumentService } from '@app/features/documents/document-service';
import {
  createHateoasError,
  FieldMessageError,
} from '@app/shared/errors/error-modal/hateoas-error';
import { ErrorService } from '@app/shared/errors/error.service';
import { catchError, finalize, from, map, mergeMap, throwError, toArray } from 'rxjs';

@Component({
  selector: 'app-upload-document-button',
  imports: [
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './upload-document-button.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./upload-document-button.scss'],
})
export class UploadDocumentButton {
  tooltip = input<string>('');
  progress = input<'bar' | 'spinner'>('bar');
  uploaded = output<{ id: string }[]>();

  // allow 1 or multiple files to be selected
  multiple = input<boolean>(false);
  // set restrictions on which MIME types are acceptable
  allowedTypes = input.required<string[]>();
  disabled = input<boolean>(false);

  documentService = inject(DocumentService);
  errorService = inject(ErrorService);
  cdr = inject(ChangeDetectorRef);

  protected isPending = false;

  clearInput($event: Event) {
    const input = $event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;
    input.value = '';
  }

  onSelected(e: Event) {
    if (this.disabled() || this.isPending) {
      return;
    }

    const target = e.target as HTMLInputElement;
    const files = target.files ? Array.from(target.files) : [];
    if (files.length === 0) {
      return;
    }

    this.isPending = true;
    this.cdr.detectChanges();

    // Reset the input value so the same file can be selected again later.
    target.value = '';

    from(files)
      .pipe(
        mergeMap((file) =>
          this.documentService.createDocument(file).pipe(
            map((res) => ({ id: res.id })),
            catchError((error) => throwError(() => ({ file, error }))),
          ),
        ),
        toArray(),
        finalize(() => this.clearPending()),
      )
      .subscribe({
        next: (documents) => {
          this.uploaded.emit(documents);
        },
        error: (uploadError: { file?: File; error?: unknown }) => {
          const fieldErrors: FieldMessageError[] = [
            {
              field: uploadError.file?.name ?? 'document',
              message: `${String(uploadError.error)}`,
            },
          ];
          this.errorService.makeErrorMessage(
            createHateoasError(
              `Erreur lors de l'envoi du document ${uploadError.file?.name ?? ''}`,
              {
                errors: fieldErrors,
              },
            ),
          );
        },
      });
  }
  private clearPending(): void {
    this.isPending = false;
    this.cdr.detectChanges();
  }
}
